import { FC, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { TableCell, TableRow, CircularProgress } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass, faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { capitalize } from 'lodash-es';
import { BoxV2 as Box, Flex } from 'portal-commons';

import { convertTimeWithTimezone } from '../../../utils/time';
import { remapTCRStatus, formatTitleCase } from '../utils';
import { CampaignListItem } from '@/apis/core/types';
import { Menu } from '@/components';

interface RcsCampaignsByPrimaryDcasListingRowProps {
  campaign: CampaignListItem;
  onActionClick?: (props: {
    type: 'accept' | 'decline';
    campaign: CampaignListItem;
  }) => void;
  processing?: boolean;
  permissions?: {
    accept?: boolean;
    decline?: boolean;
  };
}

const RcsCampaignsByPrimaryDcasListingRow: FC<
  RcsCampaignsByPrimaryDcasListingRowProps
> = ({ campaign, processing = false, onActionClick, permissions }) => {
  const history = useHistory();
  const location = useLocation();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const getCampaignStatusIcon = (status: string | undefined) => {
    switch (status) {
      case 'pending':
        return <FontAwesomeIcon icon={faHourglass} fontSize={12} />;
      case 'accepted':
        return <FontAwesomeIcon icon={faCheck} fontSize={12} />;
      default:
        return null;
    }
  };

  const statusIcon = (sharingStatus) => {
    if (!sharingStatus) return null;
    return (
      <Flex sx={{ alignItems: 'center', gap: 'xs' }}>
        <div>
          {getCampaignStatusIcon(
            campaign.secondarySharingStatus?.toLowerCase()
          )}
        </div>
        <div>{capitalize(sharingStatus)}</div>
      </Flex>
    );
  };

  return (
    <TableRow
      data-testid="RcsCampaignsByPrimaryDcasListingRow"
      onClick={() =>
        history.push({
          pathname: `/rcs-dca-campaigns/${campaign.uid}`,
          state: {
            goBackPage: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        })
      }
    >
      <TableCell
        align="left"
        data-testid="RcsCampaignsByPrimaryDcasListingRowId"
      >
        {campaign.uid ? campaign.uid : campaign.id}
      </TableCell>
      {/* <TableCell
        align="left"
        style={{ minWidth: 120 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowDisplayName"
      >
        {campaign.displayName}
      </TableCell> */}
      <TableCell
        align="left"
        style={{ minWidth: 70 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowBrandId"
      >
        {campaign.brandUid}
      </TableCell>

      <TableCell
        align="left"
        style={{
          minWidth: 150,
          maxWidth: 200,
          wordBreak: 'break-all',
        }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowBrandName"
      >
        {campaign.brandName || 'N/A'}
      </TableCell>
      <TableCell
        align="left"
        style={{ minWidth: 150 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowBillingCategory"
      >
        {formatTitleCase(campaign.billingCategory) || 'N/A'}
      </TableCell>
      <TableCell
        align="left"
        data-testid="RcsCampaignsByPrimaryDcasListingRowUsecase"
        style={{ minWidth: 160 }}
      >
        {campaign.usecase ? campaign.usecase : 'N/A'}
      </TableCell>
      <TableCell
        align="left"
        style={{ minWidth: 120, wordBreak: 'break-all' }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowPrimaryDcaName"
      >
        {campaign.primaryDcaName || 'N/A'}
      </TableCell>
      <TableCell
        align="left"
        style={{ minWidth: 120 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowMnoList"
      >
        {campaign.mnoList && campaign.mnoList.length
          ? campaign.mnoList.map((mno) => mno.name).join(', ')
          : 'N/A'}
      </TableCell>

      <TableCell
        align="left"
        style={{ minWidth: 85 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowSharedOnDate"
      >
        {campaign.sharedOnDate
          ? convertTimeWithTimezone(campaign.sharedOnDate, 'MM/DD/YYYY')
          : '-'}
      </TableCell>
      <TableCell
        align="left"
        style={{ minWidth: 81 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowCampaignStatus"
      >
        {remapTCRStatus(campaign.status)}
      </TableCell>
      <TableCell
        align="left"
        style={{ minWidth: 150 }}
        data-testid="RcsCampaignsByPrimaryDcasListingRowAcceptanceStatus"
      >
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          {statusIcon(campaign.secondarySharingStatus)}
          <Flex sx={{ alignItems: 'center' }}>
            {processing ? (
              <CircularProgress size="20px" style={{ color: '#0091B3' }} />
            ) : (
              <>
                {campaign.secondarySharingStatus === 'PENDING' &&
                  campaign.status !== 'EXPIRED' && (
                    <Box
                      role="button"
                      onClick={handleMenuClick}
                      sx={{
                        cursor: 'pointer',
                        pr: '10px',
                        pl: '16px',
                        width: '44px',
                        '&:hover svg': {
                          color: 't.orange500',
                        },
                      }}
                      data-testid="rcsCampaignsByPrimaryDcasListingRowActionButton"
                    >
                      <FontAwesomeIcon
                        icon={faEllipsisVertical}
                        fontSize="16"
                      />
                      <Menu
                        open={!!menuAnchorEl}
                        anchorEl={menuAnchorEl}
                        onClose={handleMenuClose}
                        onItemClick={(value: string) =>
                          onActionClick?.({
                            type: value as 'accept' | 'decline',
                            campaign,
                          })
                        }
                        sx={{ minWidth: 60 }}
                        data-testid="menuList"
                      >
                        <Flex sx={{ flexDirection: 'column' }}>
                          <Menu.MenuItem
                            value="accept"
                            disabled={permissions?.accept === false}
                          >
                            Accept
                          </Menu.MenuItem>
                          <Menu.MenuItem
                            value="decline"
                            disabled={permissions?.decline === false}
                          >
                            Decline
                          </Menu.MenuItem>
                        </Flex>
                      </Menu>
                    </Box>
                  )}
              </>
            )}
          </Flex>
        </Flex>
      </TableCell>
    </TableRow>
  );
};

export default RcsCampaignsByPrimaryDcasListingRow;
